import { useState, useEffect } from 'react';
import { useContacts } from '../../hooks/useContacts';
import { useDeals } from '../../hooks/useDeals';

const TaskForm = ({ task = null, onSubmit, onCancel, loading = false }) => {
  const { contacts } = useContacts();
  const { deals } = useDeals();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    priority: 'medium',
    status: 'todo',
    dealId: '',
    contactId: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        dueDate: task.dueDate || '',
        priority: task.priority || 'medium',
        status: task.status || 'todo',
        dealId: task.dealId || '',
        contactId: task.contactId || ''
      });
    }
  }, [task]);

  const priorities = ['low', 'medium', 'high', 'urgent'];
  const statuses = ['todo', 'in_progress', 'completed'];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Title *
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => {
            setFormData({ ...formData, title: e.target.value });
            setErrors({ ...errors, title: undefined });
          }}
          className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all ${
            errors.title
              ? 'border-red-500 bg-red-50 dark:bg-red-900/10'
              : 'border-slate-300 dark:border-border-dark bg-white dark:bg-surface-dark text-slate-900 dark:text-white'
          }`}
          placeholder="e.g., Follow up with client"
        />
        {errors.title && (
          <p className="text-red-500 text-xs mt-1">{errors.title}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-slate-300 dark:border-border-dark rounded-lg text-sm bg-white dark:bg-surface-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
          placeholder="Add task details..."
        />
      </div>

      {/* Due Date and Priority Row */}
      <div className="grid grid-cols-2 gap-3">
        {/* Due Date */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Due Date *
          </label>
          <input
            type="date"
            value={formData.dueDate}
            onChange={(e) => {
              setFormData({ ...formData, dueDate: e.target.value });
              setErrors({ ...errors, dueDate: undefined });
            }}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all ${
              errors.dueDate
                ? 'border-red-500 bg-red-50 dark:bg-red-900/10'
                : 'border-slate-300 dark:border-border-dark bg-white dark:bg-surface-dark text-slate-900 dark:text-white'
            }`}
          />
          {errors.dueDate && (
            <p className="text-red-500 text-xs mt-1">{errors.dueDate}</p>
          )}
        </div>

        {/* Priority */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Priority
          </label>
          <select
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 dark:border-border-dark rounded-lg text-sm bg-white dark:bg-surface-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          >
            {priorities.map(priority => (
              <option key={priority} value={priority}>
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Status */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Status
        </label>
        <select
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          className="w-full px-3 py-2 border border-slate-300 dark:border-border-dark rounded-lg text-sm bg-white dark:bg-surface-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
        >
          {statuses.map(status => (
            <option key={status} value={status}>
              {status === 'todo' ? 'To Do' : status === 'in_progress' ? 'In Progress' : 'Completed'}
            </option>
          ))}
        </select>
      </div>

      {/* Deal Selection */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Related Opportunity (Optional)
        </label>
        <select
          value={formData.dealId}
          onChange={(e) => setFormData({ ...formData, dealId: e.target.value })}
          className="w-full px-3 py-2 border border-slate-300 dark:border-border-dark rounded-lg text-sm bg-white dark:bg-surface-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
        >
          <option value="">None</option>
          {deals.map(deal => (
            <option key={deal.id} value={deal.id}>
              {deal.title} - {deal.stage}
            </option>
          ))}
        </select>
      </div>

      {/* Contact Selection */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Related Contact (Optional)
        </label>
        <select
          value={formData.contactId}
          onChange={(e) => setFormData({ ...formData, contactId: e.target.value })}
          className="w-full px-3 py-2 border border-slate-300 dark:border-border-dark rounded-lg text-sm bg-white dark:bg-surface-dark text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
        >
          <option value="">None</option>
          {contacts.map(contact => (
            <option key={contact.id} value={contact.id}>
              {contact.name} {contact.company ? `- ${contact.company}` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-slate-300 dark:border-border-dark rounded-lg text-sm font-medium text-slate-700 dark:text-white hover:bg-slate-50 dark:hover:bg-[#233648] transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-4 py-2 bg-primary text-white rounded-lg text-sm font-bold hover:bg-blue-600 transition-colors disabled:opacity-50 shadow-sm"
        >
          {loading ? 'Saving...' : task ? 'Update Task' : 'Create Task'}
        </button>
      </div>
    </form>
  );
};

export default TaskForm;
